import React, { useState, useContext, useEffect, useMemo, JSX } from "react";
import { ModalContext } from "context/modal";
import { UserContext } from "context/user";
import { CartContext } from "context/cart";
import { AppContext } from "context/app";
import { useScreenSize } from "@pepdirect/v3/hooks/useScreenSize";
import { useRouterWithRedirect } from "hooks/useRouterWithRedirect";
import { useGenericOnClickRouter } from "hooks/useGenericOnClickRouter";
import {
  NavHeader as PepNavHeader,
  CartProps,
  LogoProps,
  NavLinksProps,
  UserProps,
  SearchBarProps,
} from "@pepdirect/v3/ui/NavHeader";
import { logNavigationClickEvent } from "@pepdirect/v3/helpers/analyticsLogger";
import { endpoints, headerPromotion } from "config";
import {
  useActiveAccountQuery,
  useAdvancedVariantSearchLazyQuery,
  VariantFragment,
} from "services/graphql/generated";
import { TagsContext } from "context/category";
import { getNavigationItems } from "helpers/navigation-items-by-tag";
import { AlgoliaSearch } from "@pepdirect/v3/ui/AlgoliaSearch";
import { convertFromCentsToDollars } from "@pepdirect/v3/helpers/currency";
import { isTenantReadOnly } from "@pepdirect/v3/helpers/tenant";

export function NavHeader(): JSX.Element {
  const { open } = useContext(ModalContext);
  const { tenant } = useContext(AppContext);
  const { currentUserId, logout } = useContext(UserContext);
  const { getSize } = useContext(CartContext);
  const { navigationSets } = useContext(TagsContext);

  const [headerKey, setHeaderKey] = useState("h");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<VariantFragment[]>([]);

  const readOnly = isTenantReadOnly(tenant);

  // Search query hook
  const [
    executeSearch,
    { data: searchQueryData, loading: searchLoading, error: searchError },
  ] = useAdvancedVariantSearchLazyQuery();

  // Update search results when data changes
  useEffect(() => {
    if (searchQueryData?.advancedVariantSearch) {
      const variants = searchQueryData.advancedVariantSearch.flatMap((result) =>
        result?.variant ? [result.variant] : []
      );
      setSearchResults(variants);
    }
  }, [searchQueryData]);

  const {
    data: accountData,
    loading: accountLoading,
    refetch: accountRefetch,
  } = useActiveAccountQuery();

  const size = getSize();
  const { pushPreserveRedirect } = useRouterWithRedirect();
  const { route } = useGenericOnClickRouter();
  const { isMobile } = useScreenSize();

  useEffect(() => {
    accountRefetch();
  }, [currentUserId, accountRefetch]);

  const tenantAllowsAlgoliaSearch =
    tenant?.userExperience?.algoliaSearchActive || false;

  const goToSignIn = () => {
    if (isMobile) {
      // HACK to reset header, should be removed later
      setHeaderKey(`${headerKey}-1`);
      pushPreserveRedirect("/sign-in");
    } else {
      open("LOGIN_MODAL");
    }
  };

  const logoutAndResetHeader = () => {
    if (isMobile) {
      // HACK to reset header, should be removed later
      setHeaderKey(`${headerKey}-1`);
    }
    logout();
  };

  const searchData: SearchBarProps = {
    showSearchBar: tenantAllowsAlgoliaSearch,
    placeholderText: ``,
    onSearchClicked: () => setIsSearchOpen(!isSearchOpen),
  };

  const userData: UserProps = {
    isLoggedIn: !!currentUserId,
    accountUrl: `${endpoints.portalFrontend}/dashboard/account/`,
    onLogInClicked: goToSignIn,
    onLogOutClicked: logoutAndResetHeader,
    showATDContact:
      accountData?.activeAccount?.tags?.includes("ATD") && !accountLoading,
  };

  const linksForV2 = useMemo(
    () =>
      [
        {
          title: "Shop All",
          link: "/category/shop-all",
        },
      ].concat(
        ...getNavigationItems(
          navigationSets,
          accountData?.activeAccount?.tags || undefined
        )
      ),
    [navigationSets, accountData?.activeAccount?.tags]
  );

  const getHeaderItems = () => {
    return linksForV2;
  };

  const logo: LogoProps = {
    logoImgLink: `${endpoints.cdn}/static-images/pepsico-shop-icons/logo-white.svg`,
    onClickHandler: (e: React.MouseEvent<HTMLAnchorElement>) => {
      route(e, currentUserId && !readOnly ? "/home" : "/");
    },
  };

  const navLinks: NavLinksProps = {
    onClickHandler: (e: React.MouseEvent<HTMLAnchorElement>) => {
      const { innerText } = e.target as HTMLElement;
      logNavigationClickEvent(currentUserId, innerText);
      route(e);
    },
    navItems: currentUserId ? getHeaderItems() : [],
  };

  const cart: CartProps = {
    numOfItems: size,
    onClickHandler: (e: React.MouseEvent<HTMLAnchorElement>) =>
      route(e, "/cart"),
  };

  return (
    <div>
      <PepNavHeader
        authUsersOnly
        key={headerKey}
        logo={logo}
        navLinks={readOnly ? { navItems: [] } : navLinks}
        promotion={headerPromotion}
        searchBar={readOnly ? undefined : searchData}
        cart={readOnly ? undefined : cart}
        user={userData}
      />

      {isSearchOpen && currentUserId && (
        <AlgoliaSearch
          onCloseSearchClicked={() => {
            setIsSearchOpen(false);
            setSearchResults([]);
          }}
          onSearch={(searchTerm: string) => {
            executeSearch({
              variables: { term: searchTerm },
            });
          }}
          results={searchResults}
          loading={searchLoading}
          error={searchError?.message || null}
          formatPrice={(price) => convertFromCentsToDollars(price)}
        />
      )}
    </div>
  );
}
